import React, { useRef, useState } from 'react';
import { X, Link as LinkIcon, Share2, Copy, CheckCircle } from 'lucide-react';
import Button from '../common/Button';
import html2canvas from 'html2canvas';
import logo from '../../assets/logo.png';

interface ShareProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    uid: string;
    username: string;
    photoURL: string;
    level: string;
    points: number;
    streak: number;
  };
}

const ShareProfileModal: React.FC<ShareProfileModalProps> = ({ isOpen, onClose, user }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedDesc, setCopiedDesc] = useState(false);

  if (!isOpen) return null;

  const profileUrl = `${window.location.origin}/profile/${user.uid}`;
  const descriptionText = `Add me as friend on LinguaLearn and let's learn together with games and challenges\n${profileUrl}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy', error);
    }
  };

  const handleCopyDescription = async () => {
    try {
      await navigator.clipboard.writeText(descriptionText);
      setCopiedDesc(true);
      setTimeout(() => setCopiedDesc(false), 2000);
    } catch (error) {
      console.error('Failed to copy', error);
    }
  };

  const handleShareImage = async () => {
    const element = printRef.current;
    if (element) {
      try {
        const imgs = element.querySelectorAll('img');
        imgs.forEach(img => { 
          img.crossOrigin = 'anonymous'; 
        });

        const canvas = await html2canvas(element, { 
          useCORS: true, 
          backgroundColor: null,
          scale: 2
        });
        
        const dataUrl = canvas.toDataURL('image/png');
        const blob = await fetch(dataUrl).then(res => res.blob());
        const file = new File([blob], `${user.username}-profile.png`, { type: 'image/png' });

        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `My LinguaLearn Profile`,
            text: descriptionText,
            files: [file],
          });
        } else {
          // Fallback: download the image
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${user.username}-profile.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          // Try to copy description to clipboard as fallback so they can paste it
          handleCopyDescription();
        }
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900">Share Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 flex justify-center">
            {/* The Social Share Card */}
            <div 
              ref={printRef} 
              className="w-full max-w-md bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white p-8 relative overflow-hidden rounded-2xl shadow-lg"
            >
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }} />
              </div>

              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="flex items-center justify-center w-full mb-6">
                  <img referrerPolicy="no-referrer" src={logo} alt="Logo" className="w-12 h-12 mr-3" crossOrigin="anonymous" />
                  <div className="font-bold text-2xl tracking-tight">LinguaLearn</div>
                </div>

                <div className="mb-4 p-1 bg-white rounded-full">
                  <img 
                    referrerPolicy="no-referrer" 
                    src={user.photoURL} 
                    alt="Avatar" 
                    className="w-24 h-24 rounded-full border-4 border-indigo-100 object-cover"
                    crossOrigin="anonymous"
                  />
                </div>

                <h3 className="text-3xl font-extrabold mb-1">{user.username}</h3>
                <p className="text-indigo-100 font-medium text-lg mb-6">{user.level} Level</p>

                <div className="flex justify-center gap-6 w-full px-4 mb-4">
                  <div className="bg-white bg-opacity-20 rounded-xl p-3 flex-1 backdrop-blur-sm">
                    <p className="text-xs uppercase tracking-wider text-indigo-100 mb-1">Points</p>
                    <p className="text-2xl font-bold">{user.points}</p>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-xl p-3 flex-1 backdrop-blur-sm">
                    <p className="text-xs uppercase tracking-wider text-indigo-100 mb-1">Streak</p>
                    <p className="text-2xl font-bold">{user.streak} 🔥</p>
                  </div>
                </div>
                
                <p className="text-sm opacity-90 mt-2 font-medium">lingualearn.web.app/profile/{user.uid}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              onClick={handleCopyLink}
              leftIcon={copiedLink ? <CheckCircle className="h-5 w-5 text-green-500" /> : <LinkIcon className="h-5 w-5" />}
              fullWidth
            >
              {copiedLink ? 'Link Copied!' : 'Copy Link Only'}
            </Button>

            <Button
              variant="outline"
              onClick={handleCopyDescription}
              leftIcon={copiedDesc ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
              fullWidth
            >
              {copiedDesc ? 'Copied!' : 'Copy Link with Invite Message'}
            </Button>

            <Button
              variant="primary"
              onClick={handleShareImage}
              leftIcon={<Share2 className="h-5 w-5" />}
              fullWidth
            >
              Share Image on Social Media
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProfileModal;
